import {setLocale} from 'react-redux-i18n';
import ReactGA from 'react-ga4';
import {parse} from 'query-string';
import Keycloak from 'keycloak-js';
import config from '../../../config';
import organisationService from '../organisation/OrganisationService';
import userService from '../user/UserService';
import {setUser} from '../../actions/user';
import {selectOrganisation} from '../../actions/organisation';
import 'url-polyfill';
import history from '../../history/history';
import RolesAdapter from './RolesAdapter';
import {MANAGER_ROLE, BACKOFFICE_ROLE, CONSULTANT_ROLE, INVITING_ROLE, EXPLORER_ROLE} from '../../utils/constant';
import {captureError, consoleLog} from '../../utils/log';
import {getOrigin} from '../../utils/path';
import organisationHistory from '../../utils/organisationHistory';

function extractUser(accessTokenPayload, permissionTokenPayload) {
  return {
    id: accessTokenPayload.sub,
    email: permissionTokenPayload.email,
    emailVerified: accessTokenPayload.email_verified,
    firstName: permissionTokenPayload.firstName,
    lastName: permissionTokenPayload.lastName,
    fullName: permissionTokenPayload.fullName,
    language: permissionTokenPayload.language,
    phoneNumber: permissionTokenPayload.phoneNumber,
    entities: permissionTokenPayload.entities,
    termsAndConditionsTimestamp: permissionTokenPayload.generalTermsAgreementTimestamp,
    privacyPolicyTimestamp: permissionTokenPayload.privacyPolicyAgreementTimestamp
  };
}

// If the token expires within MIN_VALIDITY
export const MIN_VALIDITY = 35;
export const FORCE_INVALIDITY = 31536000;

export default class LiveAuthenticationService {
  getKeycloakAdapter() {
    if (!this.keycloakAdapter) {
      this.keycloakAdapter = new Keycloak(config.keycloak);
    }

    return this.keycloakAdapter;
  }

  getRolesAdapter() {
    return this.rolesAdapter;
  }

  init(store, location) {
    const kcAdapter = this.getKeycloakAdapter();
    if (kcAdapter.authenticated) {
      return Promise.resolve();
    }

    this.rolesAdapter = new RolesAdapter(store);

    return new Promise((resolve, reject) => {
      kcAdapter.init({
        checkLoginIframe: false // https://github.com/dasniko/keycloak-reactjs-demo/issues/3
      })
        .then(authenticated => {
          if (authenticated) {
            return this.authSuccessHandler(store, parse(location.search).forceOrganisationId).then(() => resolve());
          }
          this.login(store, location);
          return Promise.resolve();
        })
        .catch(error => reject(error));
    });
  }

  async authSuccessHandler(store, forceOrganisationId) {
    const userId = this.getKeycloakAdapter().tokenParsed.sub;

    if (config.analytics) {
      // Set userId for Google Analytics tracking
      ReactGA.set({userId});
    }

    // Store organisationSave before getting role
    let storeOrganisationId = store.getState().organisation ? store.getState().organisation.organisationId : organisationHistory.getLastOrganisationId();

    if (forceOrganisationId) {
      storeOrganisationId = forceOrganisationId;
    }

    store.dispatch(selectOrganisation(null));

    return this.getRolesAdapter().updateToken(userId, FORCE_INVALIDITY)
      .then(() => organisationService.getDefaultOrganisation(userId, storeOrganisationId)
        .then(async organisation => {
          store.dispatch(selectOrganisation(organisation));

          const userOrganisations = this.roles().isBackOffice() ? [] : await this.fetchUserOrganisations(userId);
          const userDetails = await userService.getUserDetailsById(userId);
          const userExistsInArtio = await userService.doesUserExistsInArtio();

          let user = extractUser(this.getKeycloakAdapter().tokenParsed, this.getRolesAdapter().getPayload());
          user = {
            ...user,
            userOrganisations,
            options: userDetails.options,
            userExistsInArtio
          };
          store.dispatch(setUser(user));
          store.dispatch(setLocale(user.language));

          return this.getRolesAdapter().updateToken(userId, FORCE_INVALIDITY);
        }))
      .catch(error => {
        captureError('Impossible to fetch user token.', error);
        throw error;
      });
  }

  login(store, location) {
    this.getKeycloakAdapter().login({
      locale: store?.getState()?.i18n?.locale,
      idpHint: parse(location?.search)?.authProvider
    });
  }

  logout() {
    const kcAdapter = this.getKeycloakAdapter();

    if (kcAdapter.authenticated) {
      kcAdapter.logout({redirectUri: getOrigin()});
    }

    kcAdapter.init()
      .then(() => {
        kcAdapter.logout({redirectUri: getOrigin()});
      })
      .catch(() => history.push('/'));
  }

  updateTokenKeycloak(force = false) {
    return new Promise(resolve => {
      this.getKeycloakAdapter().updateToken(force ? FORCE_INVALIDITY : MIN_VALIDITY)
        .then(() => resolve(this.getKeycloakAdapter().token))
        .catch(() => {
          consoleLog('Impossible to update token Keycloak');
          this.login();
        });
    });
  }

  updateTokenRoles(force = false) {
    return new Promise((resolve, reject) => {
      const userId = this.getKeycloakAdapter().tokenParsed.sub;

      this.getRolesAdapter().updateToken(userId, force ? FORCE_INVALIDITY : MIN_VALIDITY)
        .then(() => {
          resolve(this.getRolesAdapter().getToken());
        })
        .catch(error => {
          captureError('Impossible to update token roles for the authenticated user.', error);
          return reject(error);
        });
    });
  }

  authRedirect(url) {
    return Promise.all([
      this.updateTokenKeycloak(),
      this.updateTokenRoles()
    ])
      .then(([accessToken, permissionToken]) => {
        const separator = url.indexOf('?') === -1 ? '?' : '&';
        return `${url + separator}access_token=${accessToken}&permission=${permissionToken}`;
      });
  }

  roles() {
    return {
      getCurrentOrganisationRole: () => this.getRolesAdapter().getOrganisationRole(),
      isManager: () => this.getRolesAdapter().getOrganisationRole() === MANAGER_ROLE,
      isConsultant: () => this.getRolesAdapter().getOrganisationRole() === CONSULTANT_ROLE,
      isInviting: () => this.getRolesAdapter().getOrganisationRole() === INVITING_ROLE,
      isExplorer: () => this.getRolesAdapter().getOrganisationRole() === EXPLORER_ROLE,
      hasPermission: permission => this.getRolesAdapter().getOrganisationPermissions().includes(permission),
      isBackOffice: () => this.getRolesAdapter().getGlobalRole() === BACKOFFICE_ROLE
    };
  }

  fetchUserOrganisations(userId) {
    return new Promise(resolve => {
      const searchParams = {
        query: '',
        userId,
        country: null,
        postCode: null
      };

      organisationService.search(searchParams, 0, 1000)
        .then(({organisations}) => {
          resolve(organisations);
        })
        .catch(error => {
          captureError('Error while fetching user organisations', error);
        });
    });
  }
}
